import type { Message } from '@/lib/api';

export type BubbleStatus = 'sent' | 'pending' | 'failed';

interface MessageBubbleProps {
  message: Message;
  status?: BubbleStatus;
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Renders one chat bubble with the right variant for its source/direction.
 * Variants (mock parity):
 *   - sms in     : ink-100 bg, left-aligned, bottom-left corner clipped (6px)
 *   - sms out    : ss-blue bg white text, right-aligned, bottom-right clipped
 *   - shelby     : ss-blue-light bg with border, right-aligned, bottom-right clipped
 *   - system     : centered slate text (rare; reused fallback)
 */
export function MessageBubble({ message, status = 'sent' }: MessageBubbleProps) {
  const { source, direction, body, sender_name, timestamp_iso, is_workflow_sent, attachments } =
    message;

  // Bail entirely when there's no body — GHL emits a stream of empty
  // notification stubs (Shelby first-greeting echo, opportunity stage
  // changes, etc.) that would otherwise render as empty grey bubbles
  // and look broken.
  if (!body || !body.trim()) return null;

  // GHL's `messageType` is missing on a meaningful slice of historical SMS
  // messages, so the Worker classifier returns 'system' for them. If the
  // message clearly has body text and a direction, render it as a real SMS
  // bubble — the centered fallback is reserved for true system events
  // (no body, no clear direction).
  const looksLikeChat = (direction === 'in' || direction === 'out');
  const effectiveSource = source === 'system' && looksLikeChat ? 'sms' : source;

  if (effectiveSource === 'system') {
    // True system event with body text (rare) — render centered.
    return (
      <div className="my-2 text-center text-[11px] text-ink-400 dark:text-[#64748B]">{body}</div>
    );
  }

  const isShelby = effectiveSource === 'shelby';
  const isOut = direction === 'out';
  // Outgoing rows live on the right; incoming on the left.
  const rowJustify = isOut ? 'justify-end' : 'justify-start';

  // Bubble wrapping (rebuilt 2026-07-02, InboundBubbleWordBreak):
  //   The 82% cap lives on the WRAPPER div below, never here. The bubble
  //   previously carried its own max-w-[82%], which resolved against the
  //   already-shrink-to-fit wrapper — 82% of 82%, minus this px-4 padding
  //   (border-box) — leaving ~23px of text space for a short message, so
  //   "Stop" rendered as "St/op". Reproduced + verified in a static
  //   layout harness before this change.
  //   w-fit + max-w-full — size to content, cap at the wrapper's width.
  //   break-words (overflow-wrap:break-word) — long unbroken URLs/strings
  //     still wrap inside the bubble, but unlike the previous
  //     [overflow-wrap:anywhere] it does NOT feed every character into
  //     intrinsic min-content sizing, so shrink-to-fit ancestors can
  //     never get narrower than the longest word and split it.
  let bubbleClasses = 'w-fit max-w-full min-w-0 whitespace-pre-wrap break-words rounded-3xl px-4 py-2.5 text-[15px] leading-snug shadow-sm';
  if (isShelby) {
    bubbleClasses += ' bg-ss-blue-light dark:bg-[#1E3A5F] text-ink-900 dark:text-[#DBEAFE] border border-[#DBEAFE] dark:border-[#2C4A7A] rounded-br-lg';
  } else if (isOut) {
    bubbleClasses += ' bg-ss-blue text-white rounded-br-lg';
  } else {
    bubbleClasses += ' bg-white text-ink-900 ring-1 ring-ink-200 dark:bg-[#1E293B] dark:text-[#E2E8F0] dark:ring-[#334155] rounded-bl-lg';
  }

  if (status === 'pending') bubbleClasses += ' opacity-70';
  if (status === 'failed') bubbleClasses += ' ring-1 ring-status-attention';

  const metaAlign = isOut ? 'text-right' : 'text-left';
  const metaColor = 'text-ink-400 dark:text-[#64748B]';
  const relative = formatRelative(timestamp_iso);

  // "Auto" chip on outbound workflow-sent messages. is_workflow_sent is true
  // for EVERY GHL-workflow-sent outbound — drip touches, quote recap, AND
  // MCTB recovery — and the message payload doesn't carry which workflow sent
  // it. The old label "Drip" was wrong for recovery/recap messages (Ernie
  // 2026-06-05, Item 4: a contact showing "Drip off" in the header still had
  // her MCTB recovery text badged "Drip" — looked like a contradiction). An
  // honest neutral label ("Auto" = automated message) is correct for all
  // three without claiming a specific sequence we can't actually distinguish.
  // Only shows for outbound — inbound can't be workflow-sourced.
  const showWorkflowChip = isOut && is_workflow_sent === true;

  return (
    <div className={`mb-2 flex ${rowJustify}`}>
      <div className={`flex min-w-0 max-w-[82%] flex-col ${isOut ? 'items-end' : 'items-start'}`}>
        {showWorkflowChip && (
          <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3" aria-hidden="true">
              <path d="M12 2v6M12 22v-6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M22 12h-6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" />
            </svg>
            <span>Auto</span>
          </div>
        )}
        <div className={bubbleClasses}>
          {/* STEP 5 (2026-07-19) — MMS media. A customer texting a photo of
              their car used to render as an EMPTY bubble: GHL stored the
              image fine, the PWA just never got the URLs. Images render
              inline and open full-size in a new tab; non-image attachments
              (vCard, PDF) degrade to a plain link rather than a broken img. */}
          {Array.isArray(attachments) && attachments.length > 0 && (
            <div className={body ? 'mb-2 flex flex-col gap-2' : 'flex flex-col gap-2'}>
              {attachments.map((url) =>
                /\.(jpe?g|png|gif|webp|heic|bmp)(\?|$)/i.test(url) ? (
                  <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={url}
                      alt="Photo from customer"
                      loading="lazy"
                      className="max-h-64 w-full rounded-ss-sm object-cover"
                    />
                  </a>
                ) : (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2"
                  >
                    Attachment
                  </a>
                ),
              )}
            </div>
          )}
          {body}
        </div>
        <div className={`mx-2 mb-2 mt-0.5 text-[11px] ${metaColor} ${metaAlign}`}>
          {isShelby && (
            <>
              <span className="font-semibold text-ss-blue">{sender_name || 'Shelby'}</span>
              <span> · </span>
            </>
          )}
          {status === 'pending' && <span>sending… </span>}
          {status === 'failed' && (
            <span className="font-semibold text-status-attention">failed · </span>
          )}
          {relative}
        </div>
      </div>
    </div>
  );
}
